import { allGainsForResult, calculateIncrements, calculateRawAttributes, completedStarUpgrades, fragmentConstraintIgnored, isValidWheelStage, perRankFragmentCost, perStarWheelCost, totalFragmentCount } from '@minglun/domain';
import { calculateIncrementsUtility, calculateScoreFromUtility } from './scoring.js';
export function calculateObjectiveScore(request, result) {
    const { workbench, userPlan } = request;
    const gains = allGainsForResult(workbench, result);
    const increments = calculateIncrements(workbench, userPlan.baseAttributes, gains);
    return calculateScoreFromUtility(request, calculateIncrementsUtility(request, increments));
}
export function verifySolution(request, result) {
    const errors = [];
    const { workbench, userPlan } = request;
    const wheelById = new Map(workbench.fateWheels.map((wheel) => [wheel.id, wheel]));
    const characterById = new Map(workbench.characters.map((character) => [character.id, character]));
    const categoryById = new Map(workbench.wheelCategories.map((category) => [category.id, category]));
    const inventory = new Map(userPlan.inventory.map((item) => [item.characterId, item]));
    const wheelUse = new Map();
    const fullRankWheelUse = new Map();
    const partialStarWheelUse = new Map();
    const fragmentUse = new Map();
    const seen = new Set();
    for (const selected of result.fateWheels) {
        if (seen.has(selected.fateWheelId))
            errors.push(`命轮重复：${selected.fateWheelId}`);
        seen.add(selected.fateWheelId);
        const wheel = wheelById.get(selected.fateWheelId);
        if (!wheel) {
            errors.push(`命轮不存在：${selected.fateWheelId}`);
            continue;
        }
        const category = categoryById.get(wheel.wheelCategoryId);
        if (!category) {
            errors.push(`轮盘类别不存在：${wheel.wheelCategoryId}`);
            continue;
        }
        if ((!userPlan.solver.includeDestinyWheel && category.isDestiny) || !wheel.isEnabled || !category.isEnabled)
            errors.push(`不应被求解的命轮：${wheel.name}`);
        if (!isValidWheelStage(selected.rank, selected.star, wheel.maxStar, workbench.globalSettings.maxRank))
            errors.push(`阶星状态无效：${wheel.name}`);
        const starUpgrades = completedStarUpgrades(selected.rank, selected.star, wheel.maxStar);
        for (const member of wheel.members) {
            const character = characterById.get(member.characterId);
            if (!character) {
                errors.push(`伙伴不存在：${member.characterId}`);
                continue;
            }
            const starCost = perStarWheelCost(member, category);
            wheelUse.set(character.id, (wheelUse.get(character.id) ?? 0) + starUpgrades * starCost);
            fullRankWheelUse.set(character.id, (fullRankWheelUse.get(character.id) ?? 0) + selected.rank * wheel.maxStar * starCost);
            partialStarWheelUse.set(character.id, (partialStarWheelUse.get(character.id) ?? 0) + selected.star * starCost);
            fragmentUse.set(character.id, (fragmentUse.get(character.id) ?? 0) + selected.rank * perRankFragmentCost(member, character, category));
        }
    }
    const ssrChoiceUse = workbench.characters.filter((character) => character.rarity === 'SSR').reduce((total, character) => total + Math.max(0, (wheelUse.get(character.id) ?? 0) - Math.max(0, inventory.get(character.id)?.wheelCount ?? 0)), 0);
    const expectedSsrChoices = Math.max(0, Math.floor(userPlan.solver.ssrWheelChoices));
    const suggestionById = new Map();
    for (const suggestion of result.ssrChoiceSuggestions ?? []) {
        const character = characterById.get(suggestion.characterId);
        if (!character?.isEnabled || character.rarity !== 'SSR')
            errors.push(`SSR自选建议包含无效命轮：${suggestion.characterId}`);
        if (!Number.isInteger(suggestion.count) || suggestion.count <= 0)
            errors.push(`SSR自选建议数量无效：${suggestion.characterId}`);
        if (suggestionById.has(suggestion.characterId))
            errors.push(`SSR自选建议重复：${suggestion.characterId}`);
        suggestionById.set(suggestion.characterId, suggestion.count);
    }
    const suggestedTotal = [...suggestionById.values()].reduce((total, count) => total + count, 0);
    if (expectedSsrChoices > 0 && suggestedTotal !== expectedSsrChoices)
        errors.push(`SSR自选建议数量不完整：${suggestedTotal}/${expectedSsrChoices}`);
    if (expectedSsrChoices === 0 && suggestedTotal > 0)
        errors.push('SSR自选为 0 时不应产生分配建议');
    if (result.ssrChoiceProtectionTarget !== undefined && (result.ssrChoiceProtectionTarget < 0 || result.ssrChoiceProtectionTarget > 1))
        errors.push('SSR自选随机保护目标无效');
    if (result.ssrChoiceProtectionAchieved !== undefined && (result.ssrChoiceProtectionAchieved < 0 || result.ssrChoiceProtectionAchieved > 1))
        errors.push('SSR自选随机保护结果无效');
    for (const character of workbench.characters.filter((item) => item.rarity === 'SSR')) {
        const required = Math.max(0, (wheelUse.get(character.id) ?? 0) - Math.max(0, inventory.get(character.id)?.wheelCount ?? 0));
        const suggested = suggestionById.get(character.id) ?? 0;
        if (required > suggested)
            errors.push(`SSR自选建议不足：${character.name}`);
        if (userPlan.solver.ssrChoiceBalanceEnabled && expectedSsrChoices > 0) {
            if (required !== suggested)
                errors.push(`SSR自选建议未按实际消耗生成：${character.name}`);
            if (suggested > (fullRankWheelUse.get(character.id) ?? 0))
                errors.push(`SSR自选被用于非完整阶次：${character.name}`);
            if ((partialStarWheelUse.get(character.id) ?? 0) > Math.max(0, inventory.get(character.id)?.wheelCount ?? 0))
                errors.push(`SSR自选被用于非完整阶次：${character.name}`);
        }
    }
    for (const [id, used] of wheelUse)
        if (characterById.get(id)?.rarity !== 'SSR' && used > Math.max(0, inventory.get(id)?.wheelCount ?? 0))
            errors.push(`命轮不足：${id}`);
    if (ssrChoiceUse > suggestedTotal)
        errors.push('SSR命轮自选分配不足');
    for (const [id, used] of fragmentUse) {
        const entry = inventory.get(id);
        const character = characterById.get(id);
        if (fragmentConstraintIgnored(entry, character, userPlan.solver.ignoredFragmentGroups))
            continue;
        if (used > totalFragmentCount(entry))
            errors.push(`碎片不足：${id}`);
    }
    const gains = allGainsForResult(workbench, result);
    const score = calculateObjectiveScore(request, result);
    return { valid: errors.length === 0, errors, score, finalAttributes: calculateRawAttributes(workbench, gains) };
}
//# sourceMappingURL=verify.js.map